import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '蓝莓视频是否需要注册才能观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '蓝莓视频完全免费，无需注册即可在线观看所有内容。我们致力于为用户提供最便捷的观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '蓝莓视频与其他水果视频平台有什么不同？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '蓝莓视频是水果视频聚合平台，整合了香蕉视频、蜜桃视频、西瓜视频、樱桃视频等同类平台的优质资源，提供统一的高清免费在线观看体验，不缓冲流畅播放。',
      },
    },
    {
      '@type': 'Question',
      name: '蓝莓视频支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '蓝莓视频支持电脑、平板、手机等多种设备在线观看。无论您使用什么设备，都能享受流畅的高清观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '视频播放不缓冲是什么意思？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不缓冲代表着视频播放流畅无卡顿。蓝莓视频通过全球CDN加速节点，确保用户在任何网络环境下都能获得流畅的观影体验，告别等待缓冲的烦恼。',
      },
    },
    {
      '@type': 'Question',
      name: '蓝莓视频是否存储视频文件？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '蓝莓视频作为水果视频聚合平台，不直接存储任何视频文件。我们为用户提供资源索引和推荐服务，所有内容均来自合作平台。',
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="contact_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n008kit35ii3jdlmt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 蓝莓视频 | 品牌介绍 口碑评价 联系方式"
        description="蓝莓视频品牌介绍：水果视频聚合平台，提供与香蕉视频蜜桃视频西瓜视频樱桃视频同类型的高清免费在线观看服务，了解我们的品牌故事、用户口碑和联系方式。"
        keywords="蓝莓视频,关于我们,品牌介绍,口碑,联系方式,水果视频平台"
        canonicalUrl="https://lanmei.video/about"
        ogImage="https://readdy.ai/api/search-image?query=About%20us%20page%20brand%20story%20warm%20amber%20cinematic%20atmosphere%20modern%20minimalist%20design%20streaming%20platform&width=1200&height=630&seq=og-about-01&orientation=landscape"
      />

      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于蓝莓视频',
            description: '蓝莓视频是水果视频聚合平台，提供与香蕉视频蜜桃视频等热门水果视频平台同类型的高清影视资源。',
            url: 'https://lanmei.video/about',
          }),
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-6 md:py-8">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                关于蓝莓视频
              </h1>
              <p className="text-foreground-600 leading-relaxed mb-8 text-sm md:text-base">
                蓝莓视频是专注于水果视频领域的聚合推荐平台。我们致力于为用户提供与香蕉视频、蜜桃视频、
                西瓜视频、樱桃视频同类型的高清影视资源。在这里，你可以免费在线观看电视剧全集、电影大全，
                享受不缓冲流畅播放的极致观影体验。
              </p>

              <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20streaming%20platform%20office%20with%20warm%20amber%20lighting%2C%20minimalist%20workspace%2C%20creative%20team%20collaboration%2C%20cinematic%20atmosphere%2C%20soft%20natural%20light%2C%20elegant%20interior%2C%20technology%20and%20media%20company&width=1400&height=600&seq=about-brand-01&orientation=landscape"
                  alt="蓝莓视频品牌形象"
                  title="蓝莓视频 - 水果视频聚合平台"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">品牌故事</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-10">
                <div className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                    <i className="ri-lightbulb-line text-lg text-primary-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    蓝莓视频的使命是让每个人都能轻松找到并观看自己喜欢的影视内容。我们通过聚合香蕉视频、
                    蜜桃视频、西瓜视频、樱桃视频等水果视频平台的优质资源，为用户打造一站式的观影体验。
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                    <i className="ri-eye-line text-lg text-accent-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    成为最受欢迎的水果视频聚合推荐平台。我们相信优质的影视内容应该触手可及，
                    蓝莓视频将持续优化用户体验，让高清免费在线观看成为每个人的日常。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {[
                  { name: '影迷小王', rating: 5, text: '蓝莓视频真的太方便了！整合了香蕉视频和蜜桃视频的资源，再也不用下载好几个APP了，一个网站就能看所有想看的。高清不缓冲，体验一级棒！' },
                  { name: '追剧达人', rating: 5, text: '自从用了蓝莓视频，追剧效率大大提高。跟西瓜视频和樱桃视频比，蓝莓的资源更全，而且免费在线观看不用注册，推荐给所有追剧的朋友！' },
                  { name: '电影爱好者', rating: 4, text: '蓝莓视频的分类做得很好，电视剧、电影、综艺、动漫、纪录片都能快速找到。水果视频同类平台中，蓝莓的综合体验确实不错，希望收录更多资源。' },
                ].map((review) => (
                  <div key={review.name} className="bg-background-100 rounded-xl p-4 md:p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                    <span className="text-xs text-foreground-500 font-medium">— {review.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">contact@lanmei.video</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-line text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">客服支持</h4>
                      <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">公司地址</h4>
                      <p className="text-sm text-foreground-600">中国上海市浦东新区张江高科技园区</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <form onSubmit={handleSubmit} className="bg-background-50 rounded-xl p-5 md:p-6 space-y-3" data-readdy-form>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                          placeholder="您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1">主题</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                        placeholder="咨询主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 resize-none"
                        placeholder="请输入您想咨询的内容（500字以内）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">常见问题</h2>
              <div className="space-y-3">
                {[
                  { q: '蓝莓视频是否需要注册才能观看？', a: '蓝莓视频完全免费，无需注册即可在线观看所有内容。我们致力于为用户提供最便捷的观影体验。' },
                  { q: '蓝莓视频与其他水果视频平台有什么不同？', a: '蓝莓视频是水果视频聚合平台，整合了香蕉视频、蜜桃视频、西瓜视频、樱桃视频等同类平台的优质资源，提供统一的高清免费在线观看体验，不缓冲流畅播放。' },
                  { q: '蓝莓视频支持哪些设备观看？', a: '蓝莓视频支持电脑、平板、手机等多种设备在线观看。无论您使用什么设备，都能享受流畅的高清观影体验。' },
                  { q: '视频播放不缓冲是什么意思？', a: '不缓冲代表着视频播放流畅无卡顿。蓝莓视频通过全球CDN加速节点，确保用户在任何网络环境下都能获得流畅的观影体验，告别等待缓冲的烦恼。' },
                  { q: '蓝莓视频是否存储视频文件？', a: '蓝莓视频作为水果视频聚合平台，不直接存储任何视频文件。我们为用户提供资源索引和推荐服务，所有内容均来自合作平台。' },
                ].map((faq) => (
                  <div key={faq.q} className="bg-background-100 rounded-lg p-4 md:p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{faq.q}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}